import { useAndieChatControls } from '@/providers/andie-chat-provider';
import { AutoAwesomeRounded } from '@mui/icons-material';
import {
    Button,
    ButtonProps,
    IconButtonProps,
    IconButton,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const animatedGradientBorderSx = {
    position: 'relative',
    border: '1px solid transparent',
    borderRadius: 1,
    background: (theme: Theme) =>
        `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box,
         linear-gradient(60deg, ${theme.palette.andie.gradient.join(', ')}) border-box`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    backgroundSize: '100% 100%, 250% 250%',
    animation: 'animated-gradient 6s ease alternate infinite',
    color: (theme: Theme) => theme.palette.andie.main,
    minWidth: 0,
    '@keyframes animated-gradient': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
    },
} satisfies SxProps<Theme>;

// Matches the disabled treatment of the other Andie controls (see AndieChatModeSelector).
const busySx = {
    opacity: 0.45,
    cursor: 'default',
} satisfies SxProps<Theme>;

const mergeSx = (...parts: Array<SxProps<Theme> | undefined | false>): SxProps<Theme> =>
    parts.filter((part): part is SxProps<Theme> => Boolean(part)) as SxProps<Theme>;

const AndieButton = memo(
    ({
        osx,
        sx,
        children,
        title,
        loading,
        allowWhileBusy,
        onClick,
        ...buttonProps
    }: {
        osx?: SxProps<Theme>;
        sx?: SxProps<Theme>;
        children?: React.ReactNode;
        title?: React.ReactNode;
        /**
         * Spinner for work this button itself started. Never derived from the global chat status:
         * a run started elsewhere must not blank out every Andie button on the page.
         */
        loading?: boolean | null;
        /**
         * Opt out of the busy state, for buttons that stay usable during a run: the drawer toggle,
         * decorative icons, and anything calling `startNewConversation` (which stops the run first).
         */
        allowWhileBusy?: boolean;
    } & Omit<ButtonProps, 'title'>) => {
        const { isLoading } = useAndieChatControls();
        const { t } = useTranslation();

        // The provider drops messages sent while a run streams, so a button that calls sendMessage
        // is inert until it finishes. Say so instead of letting the click land on nothing.
        const busy = !allowWhileBusy && isLoading;

        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLButtonElement>) => {
                if (busy) {
                    event.preventDefault();
                    return;
                }
                onClick?.(event);
            },
            [busy, onClick],
        );

        // aria-disabled, not `disabled`: a disabled button fires no pointer events, so the tooltip
        // explaining the busy state would never open (and MUI warns about the disabled child).
        const busyProps = busy ? { 'aria-disabled': true, disableRipple: true } : {};
        const mergedSx = mergeSx(animatedGradientBorderSx, osx, sx, busy && busySx);

        return (
            <Tooltip title={busy ? t('andie.busy') : title}>
                {children ? (
                    <Button
                        {...buttonProps}
                        {...busyProps}
                        onClick={handleClick}
                        loading={loading ?? undefined}
                        sx={mergedSx}
                    >
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 'inherit',
                            }}
                        >
                            {children}
                        </Typography>
                    </Button>
                ) : (
                    <IconButton
                        children={buttonProps.startIcon || buttonProps.endIcon || <AutoAwesomeRounded />}
                        {...(buttonProps as IconButtonProps)}
                        {...busyProps}
                        onClick={handleClick}
                        sx={mergedSx}
                    />
                )}
            </Tooltip>
        );
    },
);

export default AndieButton;
